/** Commenting on a living document: the shared state and the helpers pages use with it.
 * The provider is in ReviewProvider.tsx. One provider can hold several documents - the
 * dashboard holds one per row - each keyed ``project:<id>``.
 * See docs/Y2026-Q4/design-review-mode.md section 3. */
import { createContext, useContext } from "react";

import type { ReviewAnchor, ReviewLevel } from "../api/hooks/useReview";
import type { OnReply } from "../components/RunItemCards";

export interface ReviewState {
  /** False on an earlier version: only the current document takes comments. */
  available: boolean;
  /** The document a selection outside any ``data-review-doc`` element is about. */
  defaultDoc: string | null;
  /** Marks a portalled panel opened under this provider (``REVIEW_PORTAL``): another
   * provider on the page, such as the docked conversation's, leaves a selection in it be. */
  portalId: string;
  /** ``body`` prefills the comment, e.g. with the verb of the chip that opened it;
   * ``from`` is that chip, so the composer opens beside it. */
  startComment: (
    docKey: string,
    anchor: ReviewAnchor,
    body?: string,
    from?: Element,
  ) => void;
}

export const ReviewContext = createContext<ReviewState | null>(null);

/** The comment button, composer and notices: pressing them must not close what the
 * selection is in. */
export const REVIEW_CHROME = "[data-review-chrome]";

/** Marks a panel rendered outside the page (a popover in a portal) that takes comments
 * as part of the element it opened from, valued with its provider's ``portalId``. */
export const REVIEW_PORTAL = "data-review-portal";

/** Gives a portalled ``panel`` the review metadata of the element ``from`` sits in, so a
 * selection inside it is anchored there by the provider ``portalId`` names. */
export const inheritReviewAnchor = (from: Element, panel: HTMLElement, portalId: string) => {
  const doc = from.closest<HTMLElement>("[data-review-doc]")?.dataset.reviewDoc;
  const block = from.closest<HTMLElement>("[data-review-level]");
  if (!block) return;
  panel.setAttribute(REVIEW_PORTAL, portalId);
  if (doc) panel.dataset.reviewDoc = doc;
  for (const key of [
    "reviewLevel",
    "reviewBlock",
    "reviewAgent",
    "reviewItem",
  ] as const) {
    panel.dataset[key] = block.dataset[key] ?? "";
  }
};

export const useReview = (): ReviewState | null => useContext(ReviewContext);

const LEVEL_LABEL: Record<ReviewLevel, string> = {
  dashboard_row: "Dashboard row",
  specialist_summary: "Status summary",
  specialist_details: "Detailed status",
  briefing: "Briefing",
  attributes: "Attributes",
  conversation_reply: "Reply in a conversation",
  agent_feedback: "Answer to the agent",
  operator_change: "Change to a next action",
  automated_review: "Automated review finding",
  amazon_item: "Needed on the Amazon overview",
  amazon_step: "Step on the Amazon overview",
  amazon_answer: "Amazon's answer to check",
};

export const anchorLabel = (anchor: ReviewAnchor): string =>
  [LEVEL_LABEL[anchor.level], anchor.block].filter(Boolean).join(" > ");

/** The metadata a reviewable element carries, so a selection inside it is anchored to
 * the right level, specialist and item. */
export const reviewAttrs = (
  level: ReviewLevel,
  block: string,
  {
    agent = "",
    itemId = "",
    doc,
  }: { agent?: string; itemId?: string; doc?: string } = {},
): Record<string, string> => ({
  "data-review-level": level,
  "data-review-block": block,
  "data-review-agent": agent,
  "data-review-item": itemId,
  ...(doc ? { "data-review-doc": doc } : {}),
});

// The quote a card's chip builds: "> Re: ...: [Kind] heading (id)".
const CARD_QUOTE = /\[([^\]]+)\] (.*) \(([^)]+)\)\s*$/m;

/** An item card's chip, as a comment on that item; otherwise the page's own handler
 * (null where the page has none). */
export const useReviewOnReply = (
  level: ReviewLevel,
  agent: string,
  fallback: OnReply,
): OnReply => {
  const review = useReview();
  const doc = review?.defaultDoc;
  if (!review?.available || !doc) return fallback;
  return (quote: string, from?: Element) => {
    const match = CARD_QUOTE.exec(quote);
    // A chip like Apply or Reject adds its verb under the quote's first line.
    const verb = quote.split("\n").slice(1).join("\n").trim();
    review.startComment(
      doc,
      {
        level,
        block: match
          ? `${match[1]}: ${match[2]}`
          : quote.replace(/^>\s*Re:\s*/, "").trim(),
        quote: "",
        prefix: "",
        suffix: "",
        item_id: match ? match[3] : "",
        agent,
      },
      verb,
      from,
    );
  };
};
